/**
 * Dashboard Timeline - Condensed timeline view for dashboard
 * Matches ScheduleEditor style with merged cells and no swap line
 */

import dayjs from 'dayjs'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from 'vnetwork-ui'

import { generatePathDatacentral, paths } from '@/utils/path'

import AssignmentCell from './assignment-cell'
import type { DashboardTimelineProps } from './types'
import { useDashboardTimeline } from './use-dashboard-timeline'
import { getColor } from './use-dashboard-timeline'

const DashboardTimeline: React.FC<DashboardTimelineProps> = ({
    defaultDays = 7,
    departmentId,
    weekMode = 'rolling',
    showEmptySchedules,
}) => {
    const navigate = useNavigate()
    const { t } = useTranslation('oncall')

    const {
        dateRange,
        days,
        scheduleData,
        isLoading,
        gridCols,
        minWidth,
        handlePrev,
        handleNext,
        getSpans,
    } = useDashboardTimeline(
        defaultDays,
        departmentId,
        weekMode,
        showEmptySchedules
    )

    if (isLoading) {
        return (
            <div className="animate-pulse">
                <div className="h-[200px] bg-gray-100 rounded" />
            </div>
        )
    }

    return (
        <div className="flex flex-col">
            {/* Navigation Header */}
            <div className="flex items-center gap-2 mb-2">
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handlePrev}
                    className="!p-1 !min-w-0"
                >
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs font-medium min-w-[160px] text-center">
                    {dayjs(dateRange.start).format('MMM D')} -{' '}
                    {dayjs(dateRange.end).format('MMM D, YYYY')}
                </span>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleNext}
                    className="!p-1 !min-w-0"
                >
                    <ChevronRight className="w-4 h-4" />
                </Button>
            </div>

            {/* Empty State */}
            {scheduleData.length === 0 ? (
                <div className="border rounded-sm p-4 text-center text-gray-400 text-xs">
                    Không tìm thấy phân công trong khoảng thời gian đã chọn
                </div>
            ) : (
                /* Timeline Grid */
                <div className="border rounded-sm overflow-auto">
                    <div style={{ minWidth }}>
                        {/* Header Row */}
                        <div
                            className="grid border-b sticky top-0 bg-white z-10"
                            style={{ gridTemplateColumns: gridCols }}
                        >
                            <div className="px-2 py-1.5 border-r bg-white text-[10px] font-semibold text-gray-600">
                                {t('schedule', 'Schedule')}
                            </div>
                            {days.map((day, idx) => {
                                const isToday = day.isSame(dayjs(), 'day')
                                return (
                                    <div
                                        key={idx}
                                        className={`border-r last:border-r-0 px-1 py-1.5 text-center ${isToday ? 'bg-blue-50' : 'bg-white'}`}
                                    >
                                        <span
                                            className={`block text-[10px] font-medium ${isToday ? 'text-blue-700' : 'text-gray-700'}`}
                                        >
                                            {day.format('ddd')}
                                        </span>
                                        <span
                                            className={`block text-[10px] ${isToday ? 'text-blue-600' : 'text-gray-500'}`}
                                        >
                                            {day.format('D')}
                                        </span>
                                    </div>
                                )
                            })}
                        </div>

                        {/* Schedule Rows */}
                        {scheduleData.map((schedule) => {
                            const rotations = Object.values(schedule.rotations)
                            const hasRotations = rotations.length > 0
                            const colors = getColor(schedule.scheduleId)

                            return (
                                <div
                                    key={schedule.scheduleId}
                                    className="border-b last:border-b-0"
                                >
                                    {/* Schedule Header */}
                                    <div
                                        className="grid bg-gray-50"
                                        style={{
                                            gridTemplateColumns: gridCols,
                                        }}
                                    >
                                        <div
                                            className="px-2 py-1 border-r flex items-center gap-1.5 cursor-pointer hover:bg-gray-100"
                                            onClick={() =>
                                                navigate(
                                                    generatePathDatacentral(
                                                        paths.oncallScheduleDetail,
                                                        {
                                                            id: schedule.scheduleId,
                                                        }
                                                    )
                                                )
                                            }
                                        >
                                            <div
                                                className="w-2 h-2 rounded-full"
                                                style={{
                                                    backgroundColor:
                                                        colors.border,
                                                }}
                                            />
                                            <span className="text-[10px] font-semibold text-gray-700 truncate">
                                                {schedule.scheduleName}
                                            </span>
                                        </div>
                                        {days.map((_, idx) => (
                                            <div
                                                key={idx}
                                                className="border-r last:border-r-0"
                                            />
                                        ))}
                                    </div>

                                    {/* Rotation Rows */}
                                    {hasRotations
                                        ? rotations.map((rotation) => {
                                              const spans = getSpans(
                                                  rotation.assignments,
                                                  schedule.scheduleId,
                                                  rotation.rotationId
                                              )
                                              const rotationColors = getColor(
                                                  rotation.rotationId
                                              )

                                              return (
                                                  <div
                                                      key={rotation.rotationId}
                                                      className="grid border-t"
                                                      style={{
                                                          gridTemplateColumns:
                                                              gridCols,
                                                      }}
                                                  >
                                                      <div className="px-2 py-1 border-r flex items-center min-h-[28px] bg-white">
                                                          <span className="text-[10px] text-gray-500 truncate pl-3">
                                                              {
                                                                  rotation.rotationName
                                                              }
                                                          </span>
                                                      </div>
                                                      {spans.map(
                                                          (span, idx) => (
                                                              <AssignmentCell
                                                                  key={idx}
                                                                  span={span}
                                                                  colors={
                                                                      rotationColors
                                                                  }
                                                              />
                                                          )
                                                      )}
                                                  </div>
                                              )
                                          })
                                        : /* Legacy assignments (no rotation) */
                                          (() => {
                                              const spans = getSpans(
                                                  schedule.legacyAssignments,
                                                  schedule.scheduleId
                                              )
                                              return (
                                                  <div
                                                      className="grid border-t"
                                                      style={{
                                                          gridTemplateColumns:
                                                              gridCols,
                                                      }}
                                                  >
                                                      <div className="px-2 py-1 border-r flex items-center min-h-[28px] bg-white">
                                                          <span className="text-[10px] text-gray-500 truncate pl-3">
                                                              {t(
                                                                  'assignments',
                                                                  'Assignments'
                                                              )}
                                                          </span>
                                                      </div>
                                                      {spans.map(
                                                          (span, idx) => (
                                                              <AssignmentCell
                                                                  key={idx}
                                                                  span={span}
                                                                  colors={
                                                                      colors
                                                                  }
                                                              />
                                                          )
                                                      )}
                                                  </div>
                                              )
                                          })()}
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

export default DashboardTimeline
